import { contentKeys } from "./keys";
import { useEffect, useMemo, useState } from "react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Input } from "../components/ui/input";
import { api } from "./api";
import { useCatalog } from "./catalog";

const KINDS = ["DGU", "Monolithic", "Laminated", "TGU"];
let CACHE = null;

const useGlassList = () => {
  const [list, setList] = useState(CACHE);
  useEffect(() => {
    if (!CACHE) api.get("/glass").then((r) => { CACHE = r.data.items; setList(CACHE); });
  }, []);
  return list || [];
};

export const GlassStack = ({ glass }) => {
  if (!glass?.pane_detail) return null;
  const layers = [];
  glass.pane_detail.forEach((p, i) => {
    layers.push({ w: p.t, c: p.laminated ? "#67e8f9" : "#a5f3fc", label: p.label });
    if (glass.cavities[i] !== undefined) layers.push({ w: glass.cavities[i], c: "#27272a", label: `${glass.cavities[i]} Air` });
  });
  return (
    <div className="flex h-8 overflow-hidden rounded border border-zinc-700" data-testid="glass-stack" title={glass.name}>
      {contentKeys(layers, (l) => `${l.label}:${l.w}`).map((key, i) => [key, layers[i]]).map(([key, l]) => <div key={key} style={{ flex: l.w, background: l.c }} className="border-r border-zinc-900/60 last:border-r-0" title={l.label} />)}
    </div>
  );
};

export default function GlassPicker({ value, onChange, testid = "glass-picker", range = [4, 34] }) {
  const list = useGlassList();
  const catalog = useCatalog();
  const legacy = catalog?.glass_types.find((g) => g.id === value);
  const current = list.find((g) => g.id === value);
  const [kind, setKind] = useState(current?.kind || "DGU");
  const [q, setQ] = useState("");
  const [detail, setDetail] = useState(null);
  useEffect(() => { if (current) setKind(current.kind); }, [current?.kind]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (value && !legacy) api.get(`/glass/${encodeURIComponent(value)}`).then((r) => setDetail(r.data)).catch(() => setDetail(null));
  }, [value, legacy]);
  const options = useMemo(() => list.filter((g) => g.kind === kind && g.total_mm >= range[0] && g.total_mm <= range[1] && (!q || g.name.toLowerCase().includes(q.toLowerCase()) || String(g.total_mm).startsWith(q))).slice(0, 150), [list, kind, q, range[0], range[1]]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="space-y-2" data-testid={testid}>
      <div className="flex flex-wrap gap-1.5">
        {KINDS.map((k) => (
          <button key={k} type="button" data-testid={`glass-kind-${k}`} onClick={() => setKind(k)} className={`rounded border px-2 py-1 text-[11px] transition-colors duration-150 ${kind === k ? "border-sky-500 bg-sky-500/10 text-white" : "border-zinc-800 text-zinc-400 hover:text-white"}`}>{k}</button>
        ))}
        <span className="ml-auto font-mono text-[10px] text-zinc-500">{range[0] > 4 ? `${range[0]}–${range[1]} mm only` : `${list.length} combos · 4–32 mm`}</span>
      </div>
      <Input data-testid={`${testid}-search`} placeholder="Filter e.g. 6.38, 28, SGP" value={q} onChange={(e) => setQ(e.target.value)} className="h-8 border-zinc-800 bg-zinc-900 text-xs" />
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger data-testid={`${testid}-select`} className="h-9 border-zinc-800 bg-zinc-900 text-left text-xs"><SelectValue placeholder="Select glass">{legacy?.name || current?.name || detail?.name || value}</SelectValue></SelectTrigger>
        <SelectContent className="max-h-72">
          {options.map((g) => <SelectItem key={g.id} value={g.id} className="text-xs">{g.total_mm} mm · {g.name}</SelectItem>)}
        </SelectContent>
      </Select>
      {detail && (
        <div className="space-y-1.5">
          <GlassStack glass={detail} />
          <p className="font-mono text-[10px] text-zinc-500" data-testid="glass-summary">{detail.total_mm} mm · {detail.weight_kg_m2} kg/m² · allowable {detail.allowable} MPa{detail.overhead_ok ? " · overhead OK" : ""}</p>
        </div>
      )}
    </div>
  );
}
